'use client'

import { useState } from 'react'
import { 
  Search, 
  FileText, 
  CreditCard,
  CalendarCheck,
  Heart, 
  User, 
  LogOut,
  Menu,
  X,
  HelpCircle,
  Star,
  Bell,
} from 'lucide-react'
import { User as UserType } from '@/types'
import { ClientTab, ClientStats } from '@/types/client'
import { useOnboarding } from '@/hooks/useOnboarding'
import { useNavigate } from 'react-router-dom'

interface ClientSidebarProps {
  activeTab: ClientTab
  onTabChange: (tab: ClientTab) => void
  onLogout: () => void
  user: UserType
  stats: ClientStats
}

export default function ClientSidebar({ 
  activeTab, 
  onTabChange, 
  onLogout, 
  user, 
  stats 
}: ClientSidebarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { resetOnboarding } = useOnboarding('client')
  const navigate = useNavigate()

  const handleHelpClick = () => {
    navigate('/help')
  }

  const handleRestartOnboarding = () => {
    resetOnboarding()
    // Optionally show a toast or notification
  }

  const navigationItems = [
    {
      id: 'browse' as const,
      name: 'Browse Properties',
      icon: Search,
      count: stats.totalProperties,
      description: 'Find your perfect home'
    },
    {
      id: 'applications' as const,
      name: 'My Applications',
      icon: FileText,
      count: stats.activeApplications,
      description: 'Track your requests'
    },
    {
      id: 'payments' as const,
      name: 'Payments',
      icon: CreditCard,
      count: stats.completedPayments,
      description: 'Manage rent payments'
    },
    {
      id: 'tenancies' as const,
      name: 'My Tenancies',
      icon: CalendarCheck,
      count: 0,
      description: 'Rent periods & expiration'
    },
    {
      id: 'saved' as const,
      name: 'Saved Properties',
      icon: Heart,
      count: stats.savedProperties,
      description: 'Your favorites'
    },
    {
      id: 'reviews' as const,
      name: 'Reviews & Ratings',
      icon: Star,
      count: 0,
      description: 'Review properties'
    },
    {
      id: 'profile' as const,
      name: 'Profile & KYC',
      icon: User,
      count: 0,
      description: 'Account settings'
    },
    {
      id: 'settings' as const,
      name: 'Notification Preferences',
      icon: Bell,
      count: 0,
      description: 'Configure email & notification settings'
    }
  ]

  const getUserDisplayName = () => {
    if (user?.kyc_data?.firstName && user?.kyc_data?.lastName) {
      return `${user.kyc_data.firstName} ${user.kyc_data.lastName}`
    }
    return user?.email || 'User'
  }

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-3 left-3 sm:top-4 sm:left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2.5 sm:p-3 bg-white rounded-xl shadow-lg border border-stone-200 hover:bg-stone-50 transition-colors touch-manipulation"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMobileMenuOpen ? <X size={20} className="text-stone-700" /> : <Menu size={20} className="text-stone-700" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-[min(16rem,85vw)] sm:w-64 bg-white border-r border-stone-200 shadow-sm transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen lg:inset-0 lg:w-64
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full min-h-0">
          {/* Header */}
          <div className="px-4 sm:px-6 pt-2 pb-4 sm:pb-6 border-b border-stone-200 flex justify-center flex-shrink-0">
            <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-16 h-16 sm:w-20 sm:h-20 object-contain" />
          </div>

          {/* User Info */}
          <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-stone-200 bg-stone-50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-semibold text-stone-900 truncate">
                  {getUserDisplayName()}
                </p>
                <p className="text-[11px] sm:text-xs text-stone-500 truncate mt-0.5">{user.email}</p>
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                    user.is_verified 
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                      : 'bg-amber-100 text-amber-700 border border-amber-200'
                  }`}>
                    {user.is_verified ? '✓ Verified' : '⏳ Pending'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 sm:px-4 py-4 sm:py-6 space-y-1 sm:space-y-1.5 overflow-y-auto overflow-x-hidden min-h-0">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isActive = activeTab === item.id
              
              return (
                <button
                  key={item.id}
                  data-onboarding={
                    item.id === 'browse' ? 'browse-properties' :
                    item.id === 'applications' ? 'applications-tab' :
                    item.id === 'saved' ? 'saved-properties-tab' :
                    item.id === 'profile' ? 'profile-tab' : undefined
                  }
                  onClick={() => {
                    onTabChange(item.id)
                    setIsMobileMenuOpen(false)
                  }}
                  className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-left transition-all duration-200 touch-manipulation ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm'
                      : 'text-stone-700 hover:bg-stone-50 hover:text-stone-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${isActive ? 'text-emerald-600' : 'text-stone-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-xs sm:text-sm font-semibold truncate ${isActive ? 'text-emerald-700' : 'text-stone-700'}`}>
                        {item.name}
                      </p>
                      <div className="ml-2 flex items-center gap-2">
                        {item.id === 'profile' && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              user.is_verified ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-amber-100 text-amber-700 border border-amber-200'
                            }`}
                            title={user.is_verified ? 'Your account is verified' : 'Verification required to apply'}
                          >
                            {user.is_verified ? 'Verified' : 'Pending'}
                          </span>
                        )}
                        {item.count > 0 && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                            isActive ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-stone-100 text-stone-600 border border-stone-200'
                          }`}>
                            {item.count}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className={`text-[11px] sm:text-xs truncate mt-0.5 ${isActive ? 'text-emerald-600' : 'text-stone-500'}`}>
                      {item.description}
                    </p>
                  </div>
                </button>
              )
            })}
          </nav>

          {/* Help Section */}
          <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-stone-200 flex-shrink-0">
            <button
              onClick={handleHelpClick}
              className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-left text-stone-700 hover:bg-stone-50 transition-all duration-200 touch-manipulation"
            >
              <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-stone-400" />
              <span className="text-xs sm:text-sm font-semibold">Help & FAQ</span>
            </button>
            <button
              onClick={handleRestartOnboarding}
              className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-left text-stone-700 hover:bg-stone-50 transition-all duration-200 mt-1 touch-manipulation"
            >
              <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-stone-400" />
              <span className="text-xs sm:text-sm font-semibold">Restart Tour</span>
            </button>
          </div>

          {/* Logout */}
          <div className="px-3 sm:px-4 py-3 sm:py-4 border-t border-stone-200 flex-shrink-0">
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-left text-red-600 hover:bg-red-50 transition-colors duration-200 font-semibold touch-manipulation"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="text-xs sm:text-sm">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

